import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString()}`;

const PLAN_PRICING = {
  creator: {
    monthly: 5999,
    yearly: 59990,
    yearlyMonthlyEquivalent: 4999,
  },
  pro: {
    monthly: 11999,
    yearly: 119990,
    yearlyMonthlyEquivalent: 9999,
  },
};

const PublicPricingPlans = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const PLAN_TOOL_SUMMARY = useMemo(
    () => ({
      free: [
        tt("tools.hooks", "Hooks"),
        tt("tools.titles", "Titles"),
        tt("tools.captions", "Captions"),
        tt("tools.hashtags", "Hashtags"),
        tt("tools.descriptions", "Descriptions"),
        tt("tools.scripts", "Scripts"),
      ],
      creator: [
        tt("tools.hookAngles", "Hook Angles"),
        tt("tools.ctas", "CTAs"),
        tt("tools.thumbnailAnalyzer", "Thumbnail Analyzer"),
        tt("tools.aiHumanizer", "AI Humanizer"),
        tt("tools.repurposer", "Repurposer"),
        tt("tools.adCopy", "Ad Copy"),
        tt("tools.carousels", "Carousels"),
        tt("tools.viralRewrite", "Viral Rewrite"),
        tt("tools.offerPositioning", "Offer Positioning"),
        tt("tools.hookScore", "Hook Score"),
        tt("tools.nigerianRewrite", "Nigerian Rewrite"),
        tt("tools.retentionIntro", "Retention Intro"),
        tt("tools.commentIdeas", "Comment Ideas"),
      ],
      pro: [
        tt("tools.trendConverter", "Trend Converter"),
        tt("tools.brandVoiceTrainer", "Brand Voice Trainer"),
        tt("tools.contentDifferentiation", "Content Differentiation"),
      ],
    }),
    [t]
  );

  const plans = useMemo(
    () => [
      {
        key: "free",
        name: tt("plans.free", "Free"),
        price: "₦0",
        period: tt("pricing.periodMonth", "/month"),
        subPrice: tt("pricing.startWithCoreWorkflow", "Start with the core workflow"),
        description: tt(
          "pricing.freeDescription",
          "Ideal for testing Hookora and getting familiar with the core workflow before unlocking more power."
        ),
        features: [
          tt("pricing.features.free.generations", "Monthly generations included"),
          tt("pricing.features.free.savedHooks", "Saved hooks included"),
          tt("pricing.features.free.coreToolsOnly", "Core tools only"),
          tt("pricing.features.free.coreToolsList", "Hooks, titles, captions, hashtags, descriptions, and scripts"),
          tt("pricing.features.free.simpleWorkflow", "Simple workflow for everyday creation"),
          tt("pricing.features.free.oneUser", "Single-user access"),
        ],
        unlockTitle: tt("pricing.coreWritingTools", "Core writing tools"),
        unlockItems: PLAN_TOOL_SUMMARY.free,
        accent: "#6b7280",
        cta: tt("pricing.startFree", "Start Free"),
      },
      {
        key: "creator",
        name: tt("plans.creator", "Creator"),
        price:
          billingCycle === "yearly"
            ? formatCurrency(PLAN_PRICING.creator.yearly)
            : formatCurrency(PLAN_PRICING.creator.monthly),
        period:
          billingCycle === "yearly"
            ? tt("pricing.periodYear", "/year")
            : tt("pricing.periodMonth", "/month"),
        subPrice:
          billingCycle === "yearly"
            ? `${tt("pricing.approx", "Approx.")} ${formatCurrency(
                PLAN_PRICING.creator.yearlyMonthlyEquivalent
              )}${tt("pricing.periodMonth", "/month")} • ${tt("pricing.twoMonthsFree", "2 months free")}`
            : tt("pricing.billedMonthly", "Billed monthly"),
        description: tt(
          "pricing.creatorDescription",
          "Ideal for individual creators who want premium tools for packaging, rewrites, retention, and stronger content quality."
        ),
        features: [
          tt("pricing.features.creator.generations", "More monthly generations"),
          tt("pricing.features.creator.savedHooks", "More saved hooks"),
          tt("pricing.features.creator.everythingInFree", "Everything in Free"),
          tt("pricing.features.creator.premiumTools", "Premium workflow tools"),
          tt("pricing.features.creator.workflowSupport", "Better workflow support"),
          tt(
            "pricing.features.creator.fullCreatorWorkflowSupport",
            "Stronger support for packaging, rewrites, retention, and execution"
          ),
        ],
        unlockTitle: tt("pricing.premiumWorkflowTools", "Premium workflow tools"),
        unlockItems: PLAN_TOOL_SUMMARY.creator,
        accent: "#2563eb",
        popular: true,
        cta: tt("pricing.chooseCreator", "Choose Creator"),
      },
      {
        key: "pro",
        name: tt("plans.pro", "Pro"),
        price:
          billingCycle === "yearly"
            ? formatCurrency(PLAN_PRICING.pro.yearly)
            : formatCurrency(PLAN_PRICING.pro.monthly),
        period:
          billingCycle === "yearly"
            ? tt("pricing.periodYear", "/year")
            : tt("pricing.periodMonth", "/month"),
        subPrice:
          billingCycle === "yearly"
            ? `${tt("pricing.approx", "Approx.")} ${formatCurrency(
                PLAN_PRICING.pro.yearlyMonthlyEquivalent
              )}${tt("pricing.periodMonth", "/month")} • ${tt("pricing.twoMonthsFree", "2 months free")}`
            : tt("pricing.billedMonthly", "Billed monthly"),
        description: tt(
          "pricing.proDescription",
          "Ideal for serious creators and small teams looking for deeper strategy tools, more volume, and stronger creative control."
        ),
        features: [
          tt("pricing.features.pro.generations", "Highest monthly generations"),
          tt("pricing.features.pro.everythingInCreator", "Everything in Creator"),
          tt("pricing.features.pro.advancedTools", "Advanced strategic tools"),
          tt("pricing.features.pro.strategySupport", "Deeper strategy support"),
          tt(
            "pricing.features.pro.futurePresets",
            "Priority access to future workflow expansion"
          ),
          tt("pricing.features.pro.prioritySupport", "Priority support"),
        ],
        unlockTitle: tt("pricing.advancedStrategicTools", "Advanced strategic tools"),
        unlockItems: PLAN_TOOL_SUMMARY.pro,
        accent: "#7c3aed",
        cta: tt("pricing.choosePro", "Choose Pro"),
      },
    ],
    [billingCycle, t, PLAN_TOOL_SUMMARY]
  );

  const planBackground = (key) => {
    if (key === "creator") return isDark ? "rgba(30,58,138,0.18)" : "#eff6ff";
    if (key === "pro") return isDark ? "rgba(88,28,135,0.18)" : "#f5f3ff";
    return isDark ? "rgba(30,41,59,0.78)" : "#ffffff";
  };

  const getUnlockPanelStyle = (planKey) => {
    if (planKey === "creator") {
      return {
        background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
        border: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
      };
    }

    if (planKey === "pro") {
      return {
        background: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
        border: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
        color: isDark ? "#e9d5ff" : "#7c3aed",
      };
    }

    return {
      background: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
      border: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      color: isDark ? "#fde68a" : "#9a3412",
    };
  };

  return (
    <section style={{ padding: "12px 20px 42px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: "22px",
          }}
        >
          <h2
            style={{
              margin: "0 0 10px",
              color: colors.text,
              fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
              lineHeight: "1.1",
            }}
          >
            {tt(
              "publicPricingHero.summaryTitle",
              "Choose the Hookora plan that fits your workflow"
            )}
          </h2>

          <p
            style={{
              margin: "0 auto",
              maxWidth: "780px",
              color: colors.textMuted,
              fontSize: "16px",
              lineHeight: "1.75",
            }}
          >
            {tt(
              "publicPricingHero.summaryDescription",
              "Start with Free, then move to Creator or Pro when you need more output, more premium tools, and stronger workflow support."
            )}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "22px",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: isDark ? "rgba(15,23,42,0.9)" : "#f8fafc",
              border: `1px solid ${colors.border}`,
              borderRadius: "999px",
              padding: "6px",
              boxShadow: colors.shadow,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "10px 16px",
                fontWeight: "700",
                cursor: "pointer",
                background: billingCycle === "monthly" ? "#2563eb" : "transparent",
                color: billingCycle === "monthly" ? "#ffffff" : colors.text,
              }}
            >
              {tt("pricing.monthly", "Monthly")}
            </button>

            <button
              type="button"
              onClick={() => setBillingCycle("yearly")}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "10px 16px",
                fontWeight: "700",
                cursor: "pointer",
                background: billingCycle === "yearly" ? "#2563eb" : "transparent",
                color: billingCycle === "yearly" ? "#ffffff" : colors.text,
              }}
            >
              {tt("pricing.yearly", "Yearly")}
            </button>

            <span
              style={{
                padding: "8px 12px",
                borderRadius: "999px",
                background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                color: isDark ? "#bfdbfe" : "#1d4ed8",
                fontWeight: "700",
                fontSize: "12px",
              }}
            >
              {tt("pricing.save17", "Save 17%")} • {tt("pricing.twoMonthsFree", "2 months free")}
            </span>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "18px",
          }}
        >
          {plans.map((plan) => {
            const unlockPanel = getUnlockPanelStyle(plan.key);

            return (
              <div
                key={plan.key}
                style={{
                  position: "relative",
                  background: planBackground(plan.key),
                  border: plan.popular
                    ? `2px solid ${plan.accent}`
                    : `1px solid ${colors.border}`,
                  borderRadius: "24px",
                  padding: "24px",
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none",
                }}
              >
                {plan.popular && (
                  <div
                    style={{
                      position: "absolute",
                      top: "14px",
                      right: "14px",
                      background: isDark ? "#e2e8f0" : "#111827",
                      color: isDark ? "#020617" : "#fff",
                      fontSize: "12px",
                      fontWeight: "700",
                      padding: "6px 10px",
                      borderRadius: "999px",
                    }}
                  >
                    {tt("pricing.mostPopular", "Most popular")}
                  </div>
                )}

                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: plan.accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {plan.name}
                </div>

                {plan.key !== "free" && billingCycle === "yearly" && (
                  <div
                    style={{
                      marginTop: "10px",
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                      color: isDark ? "#bfdbfe" : "#1d4ed8",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    {tt("pricing.save17", "Save 17%")}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "6px",
                    marginTop: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "40px", fontWeight: "800", color: colors.text }}>
                    {plan.price}
                  </span>
                  <span style={{ color: colors.textMuted }}>{plan.period}</span>
                </div>

                {plan.subPrice ? (
                  <div
                    style={{
                      marginTop: "4px",
                      color: colors.textMuted,
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    {plan.subPrice}
                  </div>
                ) : null}

                <p
                  style={{
                    color: colors.textSoft,
                    lineHeight: "1.6",
                    minHeight: "64px",
                    marginTop: "12px",
                  }}
                >
                  {plan.description}
                </p>

                <div
                  style={{
                    marginTop: "16px",
                    padding: "14px",
                    borderRadius: "16px",
                    background: unlockPanel.background,
                    border: `1px solid ${unlockPanel.border}`,
                  }}
                >
                  <div
                    style={{
                      color: unlockPanel.color,
                      fontSize: "13px",
                      fontWeight: "800",
                      marginBottom: "8px",
                    }}
                  >
                    {plan.unlockTitle}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: "8px",
                    }}
                  >
                    {plan.unlockItems.map((item) => (
                      <div
                        key={item}
                        style={{
                          color: unlockPanel.color,
                          fontSize: "13px",
                          lineHeight: "1.55",
                          fontWeight: "700",
                        }}
                      >
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: "16px", display: "grid", gap: "10px" }}>
                  {plan.features.map((feature) => (
                    <div
                      key={feature}
                      style={{
                        background: colors.panelBg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "10px 12px",
                        color: colors.textSoft,
                        fontSize: "14px",
                        lineHeight: "1.55",
                      }}
                    >
                      {feature}
                    </div>
                  ))}
                </div>

                <Link
                  to="/register"
                  style={{
                    marginTop: "20px",
                    width: "100%",
                    display: "inline-flex",
                    justifyContent: "center",
                    textDecoration: "none",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    border: "none",
                    background: `linear-gradient(135deg, ${plan.accent}, ${
                      plan.key === "pro"
                        ? "#4c1d95"
                        : plan.key === "creator"
                        ? "#1d4ed8"
                        : "#4b5563"
                    })`,
                    color: "#fff",
                    fontWeight: "700",
                    boxSizing: "border-box",
                  }}
                >
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PublicPricingPlans;